import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { api, unwrap } from '../api/client';
import type { Notification } from '../api/types';
import { useSession } from '../auth/session';

const NAV: ReadonlyArray<{ to: string; label: string; end?: boolean }> = [
  { to: '/app', label: 'Dashboard', end: true },
  { to: '/app/accounts', label: 'Social Accounts' },
  { to: '/app/automations', label: 'Automations' },
  { to: '/app/flows', label: 'Flows' },
  { to: '/app/inbox', label: 'Conversations' },
  { to: '/app/posts', label: 'Posts' },
  { to: '/app/stories', label: 'Stories' },
  { to: '/app/contacts', label: 'Contacts / Audience' },
  { to: '/app/insights', label: 'DM Insights' },
  { to: '/app/reports', label: 'Reports' },
  { to: '/app/api', label: 'API & Webhooks' },
  { to: '/app/team', label: 'Team' },
  { to: '/app/settings', label: 'Settings' },
];

export function Layout() {
  const { me, logout } = useSession();
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const path = useLocation().pathname;
  // UI-12: the drawer closes on navigation
  useEffect(() => {
    if (path) setOpen(false);
  }, [path]);
  // UI-78: and on Escape, so a drawer over the page is never a trap on a small screen
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  // UI-78: an open drawer is modal — focus moves into it, the page behind holds still, and the
  // trigger gets focus back, so a keyboard can open the menu and arrive where it was pointed
  useEffect(() => {
    if (!open) return;
    document.body.style.setProperty('overflow', 'hidden');
    document.getElementById('sidebar')?.querySelector('a')?.focus();
    return () => {
      document.body.style.removeProperty('overflow');
      toggleRef.current?.focus();
    };
  }, [open]);
  const notes = useQuery({
    queryKey: ['notifications'],
    queryFn: () =>
      unwrap(api.get<{ items: Notification[]; unread: number }>('/portal/notifications')),
    refetchInterval: 30_000,
  });
  return (
    <div className="app">
      <header className="topbar">
        <button
          ref={toggleRef}
          type="button"
          className="btn menu-toggle"
          aria-expanded={open}
          aria-controls="sidebar"
          onClick={() => setOpen((v) => !v)}
        >
          ☰ Menu
        </button>
        <span className="brand">
          <span className="brand-mark">A</span> AutoDM
        </span>
      </header>
      {/* UI-78: tapping the page outside the drawer shuts it */}
      {open ? (
        <button
          type="button"
          className="scrim"
          aria-label="Close menu"
          onPointerDown={() => setOpen(false)}
        />
      ) : null}
      <aside id="sidebar" className={`sidebar ${open ? 'open' : ''}`}>
        <div className="brand">
          <span className="brand-mark">A</span> AutoDM
        </div>
        <nav className="nav" aria-label="Main">
          {[...NAV, ...(me?.platformAdmin ? [{ to: '/app/admin', label: 'Admin' }] : [])].map(
            (n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end ?? false}
                className={({ isActive }) => (isActive ? 'active' : '')}
              >
                {n.label}
              </NavLink>
            ),
          )}
          <NavLink to="/app/notifications" className={({ isActive }) => (isActive ? 'active' : '')}>
            Notifications{' '}
            {notes.data && notes.data.unread > 0 ? (
              <span className="badge badge-danger">{notes.data.unread}</span>
            ) : null}
          </NavLink>
        </nav>
        <div className="sidebar-footer">
          <div>
            <strong>{me?.workspace.name}</strong>
          </div>
          <div>
            {me?.role} · plan <span className="badge badge-primary">{me?.workspace.plan}</span>
          </div>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => void logout()}
            style={{ paddingLeft: 0 }}
          >
            Log out
          </button>
        </div>
      </aside>
      <main className="main">
        {me?.notice ? (
          <div className="alert alert-info" role="status" data-testid="maintenance-notice">
            {me.notice}
          </div>
        ) : null}
        <Outlet />
      </main>
    </div>
  );
}
